<template>
  <div class="testimonials-wrapper">
    <div class="testimonials-section pt-16">
      <v-row justify="center">
        <v-col cols="12" md="8" class="text-center">
          <div class="badge-wrapper mb-4">
            <div class="badge testimonials-badge">
              <span>💬 Reviews</span>
            </div>
          </div>
          <h2 class="section-heading font-weight-bold mb-6" style="font-size: 2rem">
            Hecho por estudiantes, para estudiantes
          </h2>

          <span class="testimonials-subtitle">
            Ve por ti mismo lo que nuestros usuarios dicen de SeleAI
          </span>
        </v-col>
      </v-row>

      <v-row class="justify-center pb-16" style="max-width: 1400px; margin: 0 auto;">
        <v-col cols="12" md="10">
          <!-- Vista para móviles: v-carousel simple -->
          <v-carousel 
            v-if="$vuetify.breakpoint.smAndDown" 
            hide-delimiter-background 
            show-arrows-on-hover 
            height="auto"
            interval="5000"
            class="mobile-carousel"
          >
            <v-carousel-item
              v-for="testimonial in testimonials"
              :key="testimonial.name"
            >
              <v-card class="testimonial-card pa-4 pt-2 mx-auto" elevation="0" width="90%">
                <div class="d-flex justify-center">
                  <v-rating
                    :value="testimonial.rating"
                    color="amber"
                    background-color="amber lighten-3"
                    half-increments
                    readonly
                    dense
                  ></v-rating>
                </div>
                <p class="testimonial-text mb-6" style="font-size: 0.8rem; line-height: 1.5; font-weight: 400; font-style: italic; color: #424242;">
                  <span class="font-rubik black--text">{{ testimonial.quote }}</span>
                </p>
                <div class="d-flex align-center justify-start">
                  <v-avatar size="50" class="mr-3" :color="getAvatarColor(testimonial.name)">
                    <span class="white--text" style="font-size: 0.8rem; font-family: Arial, Helvetica, sans-serif !important; font-weight: 700;">
                      {{ getInitials(testimonial.name) }}
                    </span>
                  </v-avatar>
                  <div>
                    <div style="font-size: 0.8rem; font-weight: 700;">
                      {{ testimonial.name }}
                    </div>
                    <div style="font-size: 0.6rem; color: #757575;">
                      {{ testimonial.title }}
                    </div>
                  </div>
                </div>
              </v-card>
            </v-carousel-item>
          </v-carousel>
          
          <!-- Vista para escritorio: carrusel personalizado -->
          <div v-else class="advanced-carousel-container">
            <!-- Contenedor de testimonios con posicionamiento absoluto -->
            <div class="testimonials-container" ref="testimonialContainer">
              <div 
                v-for="(testimonial, index) in testimonials" 
                :key="testimonial.name"
                class="testimonial-position"
                :class="getPositionClass(index)"
                @click="handleTestimonialClick(index)"
              >
                <v-card 
                  class="testimonial-card px-4 mx-2" 
                  :class="[getCardClass(index), getPositionClass(index) !== 'center' ? 'clickable-card' : '']"
                  elevation="0"
                  style="padding-top: 14px !important; padding-bottom: 14px !important;"
                >
                  <div class="d-flex justify-center">
                    <v-rating
                      :value="testimonial.rating"
                      color="amber"
                      background-color="amber lighten-3"
                      half-increments
                      readonly
                      dense
                      :small="getPositionClass(index) !== 'center'"
                    ></v-rating>
                  </div>
                  <p 
                    :class="{ 
                      'testimonial-text': getPositionClass(index) === 'center',
                      'testimonial-text-side': getPositionClass(index) !== 'center'
                    }"
                    :style="getTextStyle(index)"
                    class="d-flex flex-column "
                  >
                    <span class="quote-content my-auto">{{ testimonial.quote }}</span>
                  </p>
                  <div class="d-flex align-center justify-start">
                    <v-avatar 
                      :size="getPositionClass(index) === 'center' ? 50 : 30" 
                      class="mr-3"
                      :color="getAvatarColor(testimonial.name)"
                    >
                      <span class="white--text" 
                      :style="{ fontSize: getPositionClass(index) === 'center' ? '1.2rem' : '0.9rem' }"
                      style="font-family: Arial, Helvetica, sans-serif !important; font-weight: 500;"
                      >
                        {{ getInitials(testimonial.name) }}
                      </span>
                    </v-avatar>
                    <div>
                      <div :style="{ 
                        fontSize: getPositionClass(index) === 'center' ? '0.8rem' : '0.6rem',
                        fontWeight: 700 
                      }">
                        {{ testimonial.name }}
                      </div>
                      <div :style="{ 
                        fontSize: getPositionClass(index) === 'center' ? '0.8rem' : '0.6rem',
                        color: '#757575' 
                      }">
                        {{ testimonial.title }}
                      </div>
                    </div>
                  </div>
                </v-card>
              </div>
            </div>
            
            <!-- Indicadores personalizados y flechas -->
            <div class="text-center pb-12">
              <div class="d-inline-flex carousel-indicators align-center">
                <v-btn
                  icon
                  small
                  class="carousel-arrow mr-2"
                  @click="prevSlide"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                
                <div 
                  v-for="(_, i) in testimonials" 
                  :key="i" 
                  class="carousel-indicator mx-1"
                  :class="{ active: activeIndex === i }"
                  @click="goToSlide(i)"
                ></div>
                
                <v-btn
                  icon
                  small
                  class="carousel-arrow ml-2"
                  @click="nextSlide"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TestimonialsSection',
  data: () => ({
    activeIndex: 0,
    autoplayInterval: null,
    isAnimating: false,
    testimonials: [
      {
        quote: "Sele.ai ha revolucionado completamente mi forma de estudiar. Los cuestionarios que genera son increíblemente útiles para repasar antes de los exámenes y el tutor IA me ha ayudado a entender conceptos complejos que no lograba captar en clase.",
        name: "Laura Martínez",
        title: "Estudiante de Medicina, Universidad de Barcelona",
        avatar: "../../assets/testimonials/testimonial1.jpg",
        rating: 5
      },
      {
        quote: "Como profesor, recomiendo SeleAI a todos mis alumnos. La plataforma les ayuda a procesar mejor la información de clase y les permite practicar de forma interactiva. He notado una mejora significativa en las calificaciones de quienes la utilizan regularmente.",
        name: "Dr. Miguel Sánchez",
        title: "Profesor de Química, Universidad Complutense de Madrid",
        avatar: "../../assets/testimonials/testimonial2.jpg",
        rating: 4.5
      },
      {
        quote: "Aprobar oposiciones requiere procesar enormes cantidades de información. Gracias a SeleAI he podido generar resúmenes concisos de temarios extensos y crear cuestionarios para evaluar mi conocimiento. ¡Fundamental para mi preparación!",
        name: "Carlos Rodríguez",
        title: "Opositor, Administración Pública",
        avatar: "../../assets/testimonials/testimonial3.jpg",
        rating: 5
      },
      {
        quote: "La función de generación de cuestionarios ha transformado la manera en que me preparo para mis exámenes. Ahora puedo identificar mis puntos débiles rápidamente y centrarme en ellos.",
        name: "Ana García",
        title: "Estudiante de Derecho, Universidad de Sevilla",
        avatar: "../../assets/testimonials/testimonial4.jpg",
        rating: 4
      },
      {
        quote: "Utilizo SeleAI diariamente para repasar conceptos antes de cada clase. Los resúmenes que genera son concisos y me ayudan a mantener toda la información organizada durante el semestre.",
        name: "Javier Pérez",
        title: "Estudiante de Ingeniería, Universidad Politécnica de Madrid",
        avatar: "../../assets/testimonials/testimonial5.jpg",
        rating: 4.5
      }
    ],
    stats: [
      { value: "500K+", label: "Estudiantes activos" },
      { value: "2.5M+", label: "Cuestionarios generados" },
      { value: "1.8M+", label: "Resúmenes creados" },
      { value: "92%", label: "Satisfacción" }
    ]
  }),
  computed: {
    groupedTestimonials() {
      const groups = [];
      for (let i = 0; i < this.testimonials.length; i++) {
        const leftIndex = (i - 1 + this.testimonials.length) % this.testimonials.length;
        const rightIndex = (i + 1) % this.testimonials.length;
        
        groups.push({
          left: this.testimonials[leftIndex],
          main: this.testimonials[i],
          right: this.testimonials[rightIndex]
        });
      }
      return groups;
    }
  },
  methods: {
    getPositionClass(index) {
      const relativePosition = (index - this.activeIndex + this.testimonials.length) % this.testimonials.length;
      
      if (relativePosition === 0) return 'center';
      if (relativePosition === 1) return 'right';
      if (relativePosition === this.testimonials.length - 1) return 'left';
      
      // Modificamos esta parte para gestionar mejor las posiciones
      if (relativePosition === 2) return 'far-right'; // Elemento que espera a la derecha
      if (relativePosition === this.testimonials.length - 2) return 'far-left'; // Elemento que salió por la izquierda
      
      // Elementos que están fuera de vista
      return 'hidden';
    },
    
    getCardClass(index) {
      const position = this.getPositionClass(index);
      
      if (position === 'center') return 'main-card';
      if (position === 'left' || position === 'right') return 'side-card';
      return '';
    },
    
    getTextStyle(index) {
      const position = this.getPositionClass(index);
      
      if (position === 'center') {
        return { 
          fontSize: '12px', 
          lineHeight: '1.5', 
          fontWeight: '400', 
          fontStyle: 'italic', 
          color: '#424242' 
        };
      } else {
        return {
          fontSize: '10px', 
          lineHeight: '1.3', 
          fontStyle: 'italic', 
          color: '#424242'
        };
      }
    },
    
    goToSlide(index) {
      if (this.isAnimating) return;
      this.isAnimating = true;
      
      // Cambiamos inmediatamente el índice activo
      this.activeIndex = index;
      
      // Permitimos la siguiente animación después de que termine la transición
      setTimeout(() => {
        this.isAnimating = false;
      }, 50); // Ajustamos a 600ms para coincidir con la duración de la transición CSS
      
      // Reiniciar el temporizador de autoplay
      this.stopAutoplay();
      this.startAutoplay();
    },
    
    nextSlide() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      
      this.activeIndex = (this.activeIndex + 1) % this.testimonials.length;
      this.isAnimating = false;
      
      // Reiniciar el temporizador de autoplay
      this.stopAutoplay();
      this.startAutoplay();
    },
    
    prevSlide() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      
      // Eliminamos el retraso innecesario que causaba problemas
      this.activeIndex = (this.activeIndex - 1 + this.testimonials.length) % this.testimonials.length;
      
      // Permitimos la siguiente animación después de que termine la transición
      setTimeout(() => {
        this.isAnimating = false;
      }, 600);
      
      // Reiniciar el temporizador de autoplay
      this.stopAutoplay();
      this.startAutoplay();
    },
    
    startAutoplay() {
      this.autoplayInterval = setInterval(() => {
        if (!this.isAnimating) {
          this.nextSlide();
        }
      }, 5000);
    },
    
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
      }
    },
    
    getInitials(name) {
      if (!name) return '';
      return name
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 1);
    },
    getAvatarColor(name) {
      if (!name) return '#4dd453';
      
      // Colores que combinan con el esquema verde de la web
      const colors = [
        '#e07d7e', // Rojo claro
        '#f6c2a2', // Naranja claro
        '#ffe6a2', // Amarillo claro
        '#aee2a4', // Verde claro
        '#a4ecb7', // Verde menta claro
        '#c5f0c2', // Verde lima claro
        '#f498b6', // Rosa
        '#b5dec5', // Verde grisáceo
        '#7bcfa9', // Verde menta
        '#8ec58e', // Verde suave
      ];
      // Usar el nombre como semilla para seleccionar un color
      const hashCode = name.split('').reduce((hash, char) => {
        return char.charCodeAt(0) + ((hash << 5) - hash);
      }, 0);
      
      return colors[Math.abs(hashCode) % colors.length];
    },
    handleTestimonialClick(index) {
      // Si es el central, no hacemos nada
      if (index === this.activeIndex) return;
      
      // Si está en posición left o right, permitimos la selección inmediata
      const position = this.getPositionClass(index);
      if (position === 'left' || position === 'right') {
        // Evitamos cualquier retraso innecesario
        if (this.isAnimating) return;
        
        this.isAnimating = true;
        this.activeIndex = index;
        
        // Permitimos una nueva animación después de que termine la transición
        setTimeout(() => {
          this.isAnimating = false;
        }, 600);
        
        // Reiniciar el temporizador de autoplay
        this.stopAutoplay();
        this.startAutoplay();
      }
      // No seleccionamos los muy lejanos (far-left, far-right) ni los ocultos
    }
  },
  mounted() {
    setTimeout(() => {
      this.startAutoplay();
    }, 500);
  },
  beforeDestroy() {
    this.stopAutoplay();
  }
}
</script>

<style scoped>
.testimonials-wrapper {
  position: relative;
  overflow: hidden;
}

.testimonials-wrapper::before {
  content: '';
  position: absolute;
  top: -300px;
  left: -300px;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  z-index: 0;
}

.testimonials-wrapper::after {
  content: '';
  position: absolute;
  bottom: -200px;
  right: -200px;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  z-index: 0;
}

.testimonials-section {
  position: relative;
  z-index: 1;
}

.badge-wrapper {
  display: flex;
  justify-content: center;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 100px;
  font-weight: 500;
  font-size: 0.875rem;
}

.testimonials-badge {
  background-color: white !important;
  color: #4b4b4b;
}

.section-heading {
  font-size: 2rem;
  line-height: 1.2;
  letter-spacing: -0.02em;
  font-family: 'Nunito', sans-serif;
}

.gradient-text {
  background: linear-gradient(135deg, #4dd453 0%, #58a700 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.testimonial-card {
  position: relative;
  border: 1px solid rgba(88, 204, 2, 0.1);
  border-radius: 20px !important;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  height: auto;
  display: flex;
  flex-direction: column;
  aspect-ratio: 4 / 3;
  background-color: white !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05) !important;
  border: 1px solid #d6e9daab !important;
  border-top: 4px solid rgb(145, 213, 147) !important;
  padding-top: 14px !important;
}

.testimonial-position.center .testimonial-card {
  background-color: #ffffff !important;
}

.testimonial-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08) !important;
  border-color: rgba(88, 204, 2, 0.2);
  transform: translateY(-5px);
}

.testimonial-text {
  font-size: 0.75rem;
  line-height: 1.4;
  font-weight: 400;
  font-style: italic;
  color: #424242;
  position: relative;
  z-index: 1;
  flex-grow: 1;
  overflow: hidden;
  transition: none !important;
}

.testimonial-text-side {
  overflow: hidden;
  transition: none;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  flex-grow: 1;
  max-height: 7.5rem;
  transition: none !important;
  font-size: 0.7rem;
}

.quote-content {
  display: inline-block;
  width: 100%;
  transition: none !important;
}

.stat-value {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 8px;
  font-family: 'Nunito', sans-serif;
}

.stat-label {
  font-size: 1.1rem;
  color: #616161;
  font-weight: 500;
  font-family: 'Nunito', sans-serif;
}

.theme--dark .testimonial-card {
  border-color: rgba(88, 204, 2, 0.05);
}

.theme--dark .testimonial-card:hover {
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  border-color: rgba(88, 204, 2, 0.1);
}

.theme--dark .testimonial-text {
  color: #e0e0e0;
}

/* Responsive Adjustments */
@media (max-width: 959px) {
  .section-heading {
    font-size: 2.5rem;
  }
  
  .testimonial-text {
    font-size: 1.25rem;
  }

  .stat-value {
    font-size: 2rem;
  }
}

@media (max-width: 599px) {
  .section-heading {
    font-size: 1.5rem;
  }

  .testimonial-text {
    font-size: 0.8rem;
  }
}

.testimonials-carousel {
  position: relative;
  overflow: visible;
}

.testimonials-group {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 20px 0;
}

.main-testimonial {
  width: 55%;
  z-index: 3;
}

.side-testimonial {
  width: 35%;
  opacity: 0.85;
  transform: scale(0.85);
  z-index: 2;
}

.main-card {
  transform: translateY(0);
  box-shadow: 0 4px 15px rgba(88, 204, 2, 0.1) !important;
  transition: all 0.3s ease;
  height: auto;
  padding: 1.2rem !important;
  border: 1px solid #d6e9daab !important;
  background-color: #ffffff !important;
  border-radius: 20px !important;
  border-top: 4px solid rgb(145, 213, 147) !important;
}

.main-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(88, 204, 2, 0.15) !important;
}

.side-card {
  background-color: white;
  height: auto;
  transition: all 0.3s ease;
  border: 1px solid #d6e9daab !important;
  border-radius: 20px !important;
  border-top: 4px solid rgb(145, 213, 147) !important;
}

.left-testimonial {
  transform: scale(0.85) translateX(20px);
  margin-right: -40px;
}

.right-testimonial {
  transform: scale(0.85) translateX(-20px);
  margin-left: -40px;
}

.carousel-indicators {
  margin-top: 16px;
}

.carousel-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #c7e8c8;
  cursor: pointer;
  transition: all 0.3s ease;
}

.carousel-indicator.active {
  background-color: #4DD453;
  transform: scale(1.2);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .testimonials-group {
    flex-direction: column;
  }
  
  .main-testimonial,
  .side-testimonial {
    width: 90%;
    margin: 10px 0;
  }
  
  .side-testimonial {
    display: none;
  }
  
  .testimonials-carousel {
    height: auto !important;
  }
}

.carousel-arrow {
  color: #4dd453 !important;
  transition: all 0.3s ease;
}

.carousel-arrow:hover {
  color: #58a700 !important;
  transform: scale(1.2);
}

/* Estilos para el carrusel personalizado */
.custom-carousel-container {
  position: relative;
  width: 100%;
  height: 550px;
  overflow: hidden;
}

.custom-carousel-wrapper {
  display: flex;
  width: 100%;
  height: 100%;
}

.custom-carousel-slide {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
}

/* Ajustamos los estilos existentes para que funcionen con nuestro carrusel personalizado */
.testimonials-group {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 20px 0;
}

/* Estilos responsivos para el carrusel personalizado */
@media (max-width: 960px) {
  .custom-carousel-container {
    height: auto;
    min-height: 450px;
  }
  
  .testimonials-group {
    flex-direction: column;
  }
  
  .main-testimonial,
  .side-testimonial {
    width: 90%;
    margin: 10px 0;
  }
  
  .side-testimonial {
    display: none;
  }
}

/* Estilos para el carrusel avanzado */
.advanced-carousel-container {
  position: relative;
  width: 100%;
  height: 380px;
}

.testimonials-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.testimonial-position {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Posiciones de los testimonios con animaciones mejoradas */
.testimonial-position.center {
  z-index: 3;
  opacity: 1;
  width: 40%;
  left: 50%;
  transform: translateX(-50%);
  aspect-ratio: 5 / 3;
  max-width: 400px;
}

.testimonial-position.left {
  z-index: 2;
  opacity: 0.85;
  width: 30%;
  left: 10%;
  transform: translateX(0) scale(0.85);
  aspect-ratio: 5 / 3;
  max-width: 320px;
}

.testimonial-position.right {
  z-index: 2;
  opacity: 0.85;
  width: 30%;
  right: 10%;
  left: auto;
  transform: translateX(0) scale(0.85);
  aspect-ratio: 5 / 3;
  max-width: 320px;
}

/* Nuevas clases para posiciones adicionales con transiciones suaves */
.testimonial-position.far-left {
  z-index: 1;
  opacity: 0.3;
  width: 30%;
  left: -5%;
  transform: translateX(0) scale(0.7);
}

.testimonial-position.far-right {
  z-index: 1;
  opacity: 0.3;
  width: 30%;
  right: -5%;
  left: auto;
  transform: translateX(0) scale(0.7);
}

.testimonial-position.hidden {
  opacity: 0;
  z-index: 0;
  transform: translateX(-200%) scale(0.5);
  pointer-events: none;
}

/* Eliminamos estas clases que causaban el problema */
.testimonial-position.hidden-left,
.testimonial-position.hidden-right {
  display: none;
}

/* Ajustar las clases para controlar la dirección de entrada/salida */
.testimonial-position.hidden-left {
  transform: translateX(-200%);
  opacity: 0;
}

.testimonial-position.hidden-right {
  transform: translateX(200%);
  opacity: 0;
}

/* Ajustes a las tarjetas */
.main-card {
  box-shadow: 0 4px 15px rgba(88, 204, 2, 0.1) !important;
  height: auto;
  padding: 1.2rem !important;
  background-color: white;
}

.main-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(88, 204, 2, 0.15) !important;
}

.side-card {
  background-color: rgba(255, 255, 255, 0.95);
  height: auto;
  transition: all 0.3s ease;
}

/* Ajustes responsivos */
@media (max-width: 960px) {
  .advanced-carousel-container {
    height: auto;
    min-height: 380px;
  }
  
  .testimonial-position.center {
    width: 85%;
    left: 7.5%;
    aspect-ratio: 5 / 3;
  }
  
  .testimonial-position.left,
  .testimonial-position.right {
    display: none;
  }
  
  .testimonial-card {
    aspect-ratio: 5 / 3;
    max-height: 500px;
  }
}

/* En pantallas muy pequeñas, ajustamos el aspect ratio para mejor legibilidad */
@media (max-width: 480px) {
  .testimonial-card {
    aspect-ratio: 2 / 3;
  }
  
  .testimonial-text {
    font-size: 0.8rem;
    line-height: 1.5;
  }
}

.testimonial-position.center .testimonial-card,
.testimonial-position.left .testimonial-card,
.testimonial-position.right .testimonial-card {
  width: 100%;
  height: auto;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.quote-content {
  display: inline-block;
  width: 100%;
}

/* Añadimos transición específica solo para propiedades de posicionamiento */
.testimonial-position.center,
.testimonial-position.left,
.testimonial-position.right,
.testimonial-position.far-left,
.testimonial-position.far-right,
.testimonial-position.hidden {
  transition: transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1), 
              opacity 0.5s cubic-bezier(0.25, 0.8, 0.25, 1),
              left 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Estilo para testimonios clickeables */
.clickable-card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.clickable-card:hover {
  box-shadow: 0 8px 20px rgba(88, 204, 2, 0.15) !important;
  border-color: rgba(88, 204, 2, 0.2);
}

/* Ajustamos el hover para tarjetas laterales */
.testimonial-position.left .testimonial-card:hover,
.testimonial-position.right .testimonial-card:hover {
  box-shadow: 0 8px 20px rgba(88, 204, 2, 0.15);
}

/* Mantenemos el hover original solo para la tarjeta central */
.testimonial-position.center .testimonial-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(88, 204, 2, 0.15) !important;
}

/* Estilos para el carrusel en móvil */
.mobile-carousel {
  border-radius: 24px;
  overflow: hidden;
  margin-bottom: 20px;
}

.mobile-carousel .v-window__container {
  height: auto !important;
}

.mobile-carousel .v-carousel__item {
  height: auto;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-carousel .testimonial-card {
  border: 1px solid #d6e9daab !important;
  border-radius: 20px !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05) !important;
  background-color: #f6faf5 !important;
  transition: none;
  height: 100%;
  display: flex;
  flex-direction: column;
  aspect-ratio: auto;
  max-width: 450px;
}

.mobile-carousel .testimonial-text {
  flex-grow: 1;
  display: flex;
  align-items: center;
}

/* Ajuste para las valoraciones */
.v-rating .v-icon {
  padding: 0 2px !important;
}

/* Estilos para el badge de testimonio */
.testimonials-badge {
  background: #fff;
  color: #4b4b4b;
  border-radius: 16px;
  padding: 4px 8px;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-content: center;
  justify-content: center;
  width: fit-content;
  border: 1px solid #e9e9e9
}

/* Ajustes para el avatar */
.testimonial-position.center .v-avatar {
  border: 2px solid #d6e9daab;
}
</style> 
